<template>
  <div class="modal-overlay">
    <div class="mmodal-content" style="max-width: 80vw">
      <div class="modal-header">
        <h3>{{ editingRecord ? "Edit" : "Add" }} Growth Reading</h3>
        <button type="button" class="close" @click="closeModal">&times;</button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="saveRecord">
          <div class="form-grid">
            <div class="form-group">
              <label>Date Recorded *</label>
              <input type="datetime-local" v-model="form.recorded_at" required class="form-control" />
            </div>
            <div class="form-group">
              <label>Age (months)</label>
              <input type="number" v-model="form.age_months" min="0" class="form-control" />
            </div>
            <div class="form-group">
              <label>Weight (kg)</label>
              <input type="number" v-model="form.weight_kg" step="0.1" min="0" class="form-control" />
            </div>
            <div class="form-group">
              <label>Height/Length (cm)</label>
              <input type="number" v-model="form.height_cm" step="0.1" min="0" class="form-control" />
            </div>
            <div class="form-group">
              <label>MUAC (cm)</label>
              <input type="number" v-model="form.muac_cm" step="0.1" min="0" class="form-control" />
            </div>
            <div class="form-group">
              <label>Head Circumference (cm)</label>
              <input type="number" v-model="form.head_circumference_cm" step="0.1" min="0" class="form-control" />
            </div>

            <div class="form-group">
              <label>Nutritional Status</label>
              <select v-model="form.nutritional_status" class="form-control">
                <option value="">Select</option>
                <option value="Normal">Normal</option>
                <option value="Moderate malnutrition">Moderate malnutrition</option>
                <option value="Severe malnutrition">Severe malnutrition</option>
                <option value="Overweight">Overweight</option>
              </select>
            </div>
            <div class="form-group">
              <label>Weight for Age</label>
              <input type="text" v-model="form.weight_for_age" placeholder="e.g. Normal, Underweight" class="form-control" />
            </div>
            <div class="form-group">
              <label>Height for Age</label>
              <input type="text" v-model="form.height_for_age" placeholder="e.g. Normal, Stunted" class="form-control" />
            </div>
            <div class="form-group">
              <label>Weight for Height</label>
              <input type="text" v-model="form.weight_for_height" placeholder="e.g. Normal, Wasted" class="form-control" />
            </div>
            <div class="form-group">
              <label class="checkbox-label">
                <input type="checkbox" v-model="form.oedema_present" />
                Oedema present
              </label>
            </div>

            <div class="form-group full-width">
              <label>Feeding Practices</label>
              <textarea v-model="form.feeding_practices" class="form-control" rows="2"></textarea>
            </div>
            <div class="form-group full-width">
              <label>Advice Given</label>
              <textarea v-model="form.advice_given" class="form-control" rows="2"></textarea>
            </div>
            <div class="form-group">
              <label>Next Appointment</label>
              <input type="date" v-model="form.next_appointment_date" class="form-control" />
            </div>
            <div class="form-group full-width">
              <label>Notes</label>
              <textarea v-model="form.notes" class="form-control" rows="2"></textarea>
            </div>
            <div class="form-group">
              <label>Recorded By</label>
              <input type="text" v-model="form.recorded_by" class="form-control" />
            </div>
          </div>

          <div class="modal-actions">
            <button type="button" class="btn btn-secondary" @click="closeModal">Cancel</button>
            <button type="submit" class="btn btn-primary" :disabled="saving">
              {{ saving ? "Saving..." : (editingRecord ? "Update" : "Save") + " Reading" }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { generalApiRequest } from "../../../../api/apiIndex";

export default {
  name: "GrowthRecordModal",
  props: {
    visitId: { type: [Number, String], default: null },
    editRecord: { type: Object, default: null },
  },
  data() {
    return {
      saving: false,
      editingRecord: null,
      form: this.emptyForm(),
    };
  },
  created() {
    if (this.editRecord) {
      this.editingRecord = this.editRecord;
      this.form = {
        ...this.emptyForm(),
        ...this.editRecord,
        recorded_at: this.toLocalDateTime(this.editRecord.recorded_at),
        next_appointment_date: this.editRecord.next_appointment_date
          ? this.editRecord.next_appointment_date.substring(0, 10)
          : "",
      };
    }
  },
  methods: {
    emptyForm() {
      return {
        recorded_at: this.toLocalDateTime(new Date()),
        age_months: "",
        weight_kg: "",
        height_cm: "",
        muac_cm: "",
        head_circumference_cm: "",
        nutritional_status: "",
        weight_for_age: "",
        height_for_age: "",
        weight_for_height: "",
        oedema_present: false,
        feeding_practices: "",
        advice_given: "",
        next_appointment_date: "",
        notes: "",
        recorded_by: "",
      };
    },
    toLocalDateTime(value) {
      if (!value) return "";
      const d = new Date(value);
      if (isNaN(d.getTime())) return "";
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },
    closeModal() {
      this.$emit("close");
    },
    async saveRecord() {
      this.saving = true;
      try {
        const payload = { ...this.form, visit_id: this.visitId };
        let response;
        if (this.editingRecord) {
          response = await generalApiRequest(`cwc_growth_records/${this.editingRecord.id}`, "PUT", payload, 1);
        } else {
          response = await generalApiRequest("cwc_growth_records", "POST", payload, 1);
        }
        if (response.success) {
          this.$emit("saved", response.data);
          this.closeModal();
        } else {
          alert(response.message || "Failed to save growth reading");
        }
      } catch (error) {
        console.error("Error saving growth record:", error);
        alert("An error occurred while saving the growth reading");
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.mmodal-content { background: white; border-radius: 10px; max-height: 90vh; overflow-y: auto; display: flex; flex-direction: column; }
.modal-header { padding: 20px; background: linear-gradient(135deg, #48bb78 0%, #276749 100%); color: white; display: flex; justify-content: space-between; align-items: center; }
.modal-header h3 { margin: 0; font-size: 20px; }
.close { background: rgb(233, 22, 22); border: 1px solid rgb(182, 178, 178); font-size: 1.5rem; color: white; line-height: 1; cursor: pointer; }
.modal-body { padding: 30px; overflow-y: auto; flex-grow: 1; }
.form-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; margin-bottom: 20px; }
.form-group { margin-bottom: 15px; }
.form-group.full-width { grid-column: 1 / -1; }
.form-group label { display: block; margin-bottom: 5px; font-weight: 600; color: #495057; }
.checkbox-label { display: flex !important; align-items: center; gap: 8px; margin-top: 28px; }
.form-control { width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 5px; font-size: 14px; }
.modal-actions { display: flex; justify-content: flex-end; gap: 15px; }
.btn { padding: 10px 25px; border: none; border-radius: 5px; cursor: pointer; font-weight: 600; }
.btn-secondary { background: #6c757d; color: white; }
.btn-secondary:hover { background: #5a6268; }
.btn-primary { background: #276749; color: white; }
.btn-primary:hover { background: #22543d; }
.btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
@media (max-width: 1200px) { .form-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 768px) { .form-grid { grid-template-columns: 1fr; } }
</style>
